import { Injectable, Inject, Optional } from "@angular/core";
import { API_BASE_URL } from "./service-proxies";
import {
  mergeMap as _observableMergeMap,
  catchError as _observableCatch,
} from "rxjs/operators";
import {
  Observable,
  throwError as _observableThrow,
  of as _observableOf,
} from "rxjs";
import {
  HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import * as moment from "moment";

@Injectable()
export class ApplicationServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  CreateApplication(body: CreateApplicationDto): Observable<ApplicationDto> {
    let url_ = this.baseUrl + "/api/services/app/Application/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreateApplication(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreateApplication(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<ApplicationDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationDto>;
        }),
      );
  }

  protected processCreateApplication(
    response: HttpResponseBase,
  ): Observable<ApplicationDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApplicationDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  getForEdit(id: number | undefined): Observable<CreateApplicationDto> {
    let url_ = this.baseUrl + "/api/services/app/Application/GetForEdit?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetForEdit(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetForEdit(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<CreateApplicationDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<CreateApplicationDto>;
        }),
      );
  }

  protected processGetForEdit(
    response: HttpResponseBase,
  ): Observable<CreateApplicationDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CreateApplicationDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return Success
   */
  update(body: CreateApplicationDto | undefined): Observable<ApplicationDto> {
    let url_ = this.baseUrl + "/api/services/app/Application/Update";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("put", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdate(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<ApplicationDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationDto>;
        }),
      );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<ApplicationDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApplicationDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  getAll(
    keyword: string | undefined,
    countryIds: number[] | undefined,
    statusIds: number[] | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
  ): Observable<ApplicationDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/Application/GetAll?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (countryIds === null)
      throw new Error("The parameter 'countryIds' cannot be null.");
    else if (countryIds !== undefined)
      countryIds &&
        countryIds.forEach((item) => {
          url_ += "CountryIds=" + encodeURIComponent("" + item) + "&";
        });
    if (statusIds === null)
      throw new Error("The parameter 'statusIds' cannot be null.");
    else if (statusIds !== undefined)
      statusIds &&
        statusIds.forEach((item) => {
          url_ += "StatusIds=" + encodeURIComponent("" + item) + "&";
        });
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<ApplicationDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationDtoPagedResultDto>;
        }),
      );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<ApplicationDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApplicationDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }
  /**
   * @param statusId (optional)
   * @param keyword (optional)
   * @param sorting (optional)
   * @param isDesc (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getAllForDashboard(
    statusId: number | undefined,
    keyword: string | undefined,
    sorting: string | undefined,
    isDesc: boolean | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
  ): Observable<ApplicationDashboardDtoPagedResultDto> {
    let url_ =
      this.baseUrl + "/api/services/app/Application/GetAllForDashboard?";
    if (statusId === null)
      throw new Error("The parameter 'statusId' cannot be null.");
    else if (statusId !== undefined)
      url_ += "StatusId=" + encodeURIComponent("" + statusId) + "&";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (sorting === null)
      throw new Error("The parameter 'sorting' cannot be null.");
    else if (sorting !== undefined)
      url_ += "Sorting=" + encodeURIComponent("" + sorting) + "&";
    if (isDesc === null)
      throw new Error("The parameter 'isDesc' cannot be null.");
    else if (isDesc !== undefined)
      url_ += "IsDesc=" + encodeURIComponent("" + isDesc) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAllForDashboard(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAllForDashboard(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<ApplicationDashboardDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationDashboardDtoPagedResultDto>;
        }),
      );
  }

  protected processGetAllForDashboard(
    response: HttpResponseBase,
  ): Observable<ApplicationDashboardDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 =
            ApplicationDashboardDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param date (optional)
   * @return Success
   */
  getUserActionsByDate(
    date: moment.Moment | undefined | any,
  ): Observable<DailyActionDto[]> {
    let url_ =
      this.baseUrl + "/api/services/app/Application/GetUserActionsByDate?";
    if (date === null) throw new Error("The parameter 'date' cannot be null.");
    else if (date !== undefined)
      url_ += "date=" + encodeURIComponent(date ? "" + date._i : "") + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetUserActionsByDate(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetUserActionsByDate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<DailyActionDto[]>;
            }
          } else
            return _observableThrow(response_) as any as Observable<
              DailyActionDto[]
            >;
        }),
      );
  }

  protected processGetUserActionsByDate(
    response: HttpResponseBase,
  ): Observable<DailyActionDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(DailyActionDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  getApplicationTags(id: number | undefined): Observable<string[]> {
    let url_ =
      this.baseUrl + "/api/services/app/Application/GetApplicationTags?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetApplicationTags(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetApplicationTags(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<string[]>;
            }
          } else
            return _observableThrow(response_) as any as Observable<string[]>;
        }),
      );
  }

  protected processGetApplicationTags(
    response: HttpResponseBase,
  ): Observable<string[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200.push(item);
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return Success
   */
  rquestCounsling(
    body: CounselingRequestModel | undefined,
  ): Observable<number> {
    let url_ = this.baseUrl + "/api/services/app/Application/RquestCounsling";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processRquestCounsling(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processRquestCounsling(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<number>;
            }
          } else
            return _observableThrow(response_) as any as Observable<number>;
        }),
      );
  }

  protected processRquestCounsling(
    response: HttpResponseBase,
  ): Observable<number> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : <any>null;

          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }
}

@Injectable()
export class ApplicationStatusLogServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  getAll(
    ApplicattionId: number | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
  ): Observable<ApplicationStatusLogDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/ApplicationStatusLog/GetAll?";
    if (ApplicattionId === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (ApplicattionId !== undefined)
      url_ += "ApplicattionId=" + encodeURIComponent("" + ApplicattionId) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<ApplicationStatusLogDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationStatusLogDtoPagedResultDto>;
        }),
      );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<ApplicationStatusLogDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 =
            ApplicationStatusLogDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  CreateApplicationStatusLog(
    body: AddMovementDto,
  ): Observable<ApplicationStatusLogDto> {
    let url_ = this.baseUrl + "/api/services/app/ApplicationStatusLog/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreateApplicationStatusLog(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreateApplicationStatusLog(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<ApplicationStatusLogDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationStatusLogDto>;
        }),
      );
  }

  protected processCreateApplicationStatusLog(
    response: HttpResponseBase,
  ): Observable<ApplicationStatusLogDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApplicationStatusLogDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }
}


export class CounselingRequestModel implements ICounselingRequestModel {
    firstName: string;
    lastName: string;
    gender: boolean;
    birthDate: Date;
    gradeId: number;
    educationField: string;
    average: number;
    languageCodes: number[] | undefined;
    languageLevel: number | undefined;
    isMarried: boolean;
    phoneNumber: string | undefined;
    email: string | undefined;
    referrer: string | undefined;
    consultationPurpose: string;
    slotId: number | undefined;
    reservationId: string | undefined;
    meetingTypeId: number;

    constructor(data?: ICounselingRequestModel) {
        if (data) {
            for (var property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.firstName = _data["firstName"];
            this.lastName = _data["lastName"];
            this.gender = _data["gender"];
            this.birthDate = _data["birthDate"];
            this.gradeId = _data["gradeId"];
            this.educationField = _data["educationField"];
            this.average = _data["average"];
            if (Array.isArray(_data["languageCodes"])) {
                this.languageCodes = [] as any;
                for (let item of _data["languageCodes"])
                    this.languageCodes.push(item);
            }
            this.languageLevel = _data["languageLevel"];
            this.isMarried = _data["isMarried"];
            this.phoneNumber = _data["phoneNumber"];
            this.email = _data["email"];
            this.referrer = _data["referrer"];
            this.consultationPurpose = _data["consultationPurpose"];
            this.slotId = _data["slotId"];
            this.reservationId = _data["reservationId"];
            this.meetingTypeId = _data["meetingTypeId"];
        }
    }

    static fromJS(data: any): CounselingRequestModel {
        data = typeof data === 'object' ? data : {};
        let result = new CounselingRequestModel();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["firstName"] = this.firstName;
        data["lastName"] = this.lastName;
        data["gender"] = this.gender;
        data["birthDate"] = this.birthDate;
        data["gradeId"] = this.gradeId;
        data["educationField"] = this.educationField;
        data["average"] = this.average;
        if (Array.isArray(this.languageCodes)) {
            data["languageCodes"] = [];
            for (let item of this.languageCodes)
                data["languageCodes"].push(item);
        }
        data["languageLevel"] = this.languageLevel;
        data["isMarried"] = this.isMarried;
        data["phoneNumber"] = this.phoneNumber;
        data["email"] = this.email;
        data["referrer"] = this.referrer;
        data["consultationPurpose"] = this.consultationPurpose;
        data["slotId"] = this.slotId;
        data["reservationId"] = this.reservationId;
        data["meetingTypeId"] = this.meetingTypeId;
        return data;
    }

    clone(): CounselingRequestModel {
        const json = this.toJSON();
        let result = new CounselingRequestModel();
        result.init(json);
        return result;
    }
}

export interface ICounselingRequestModel {
    firstName: string;
    lastName: string;
    gender: boolean;
    birthDate: Date;
    gradeId: number;
    educationField: string;
    average: number;
    languageCodes: number[] | undefined;
    languageLevel: number | undefined;
    isMarried: boolean;
    phoneNumber: string | undefined;
    email: string | undefined;
    referrer: string | undefined;
    consultationPurpose: string;
    slotId: number | undefined;
    reservationId: string | undefined;
    meetingTypeId: number;
}

export class DailyActionDto implements IDailyActionDto {
  id: number;
  firstName: string | undefined;
  lastName: string | undefined;
  followUpTime: string | undefined;
  statusName: string | undefined;
  countryName: string | undefined;
  visaType: string | undefined;
  statusStateType: StatusStateType;

  constructor(data?: IDailyActionDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.firstName = _data["firstName"];
      this.lastName = _data["lastName"];
      this.followUpTime = _data["followUpTime"];
      this.statusName = _data["statusName"];
      this.countryName = _data["countryName"];
      this.visaType = _data["visaType"];
      this.statusStateType = _data["statusStateType"];
    }
  }

  static fromJS(data: any): DailyActionDto {
    data = typeof data === "object" ? data : {};
    let result = new DailyActionDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["firstName"] = this.firstName;
    data["lastName"] = this.lastName;
    data["followUpTime"] = this.followUpTime;
    data["statusName"] = this.statusName;
    data["countryName"] = this.countryName;
    data["visaType"] = this.visaType;
    data["statusStateType"] = this.statusStateType;
    return data;
  }

  clone(): DailyActionDto {
    const json = this.toJSON();
    let result = new DailyActionDto();
    result.init(json);
    return result;
  }
}

export interface IDailyActionDto {
  id: number;
  firstName: string | undefined;
  lastName: string | undefined;
  followUpTime: string | undefined;
  statusName: string | undefined;
  countryName: string | undefined;
  visaType: string | undefined;
  statusStateType: StatusStateType;
}

export class ApplicationDashboardDto implements IApplicationDashboardDto {
  id: number;
  firstName: string | undefined;
  lastName: string | undefined;
  statusName: string | undefined;
  statusStateType: StatusStateType;
  countryName: string | undefined;
  visaType: string | undefined;
  lastInteractionDate: string | undefined;
  lastInteractionTime: string | undefined;
  reponsibleUserId: number;
  statusActionType: StatusActionType;
  validMovements: MovementViewDto[] | undefined;

  constructor(data?: IApplicationDashboardDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.firstName = _data["firstName"];
      this.lastName = _data["lastName"];
      this.statusName = _data["statusName"];
      this.statusStateType = _data["statusStateType"];
      this.countryName = _data["countryName"];
      this.visaType = _data["visaType"];
      this.lastInteractionDate = _data["lastInteractionDate"];
      this.lastInteractionTime = _data["lastInteractionTime"];
      this.reponsibleUserId = _data["reponsibleUserId"];
      this.statusActionType = _data["statusActionType"];
      if (Array.isArray(_data["validMovements"])) {
        this.validMovements = [] as any;
        for (let item of _data["validMovements"])
          this.validMovements.push(MovementViewDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): ApplicationDashboardDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationDashboardDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["firstName"] = this.firstName;
    data["lastName"] = this.lastName;
    data["statusName"] = this.statusName;
    data["statusStateType"] = this.statusStateType;
    data["countryName"] = this.countryName;
    data["visaType"] = this.visaType;
    data["lastInteractionDate"] = this.lastInteractionDate;
    data["lastInteractionTime"] = this.lastInteractionTime;
    data["reponsibleUserId"] = this.reponsibleUserId;
    data["statusActionType"] = this.statusActionType;
    if (Array.isArray(this.validMovements)) {
      data["validMovements"] = [];
      for (let item of this.validMovements)
        data["validMovements"].push(item.toJSON());
    }
    return data;
  }

  clone(): ApplicationDashboardDto {
    const json = this.toJSON();
    let result = new ApplicationDashboardDto();
    result.init(json);
    return result;
  }
}

export interface IApplicationDashboardDto {
  id: number;
  firstName: string | undefined;
  lastName: string | undefined;
  statusName: string | undefined;
  statusStateType: StatusStateType;
  countryName: string | undefined;
  visaType: string | undefined;
  lastInteractionDate: string | undefined;
  lastInteractionTime: string | undefined;
  reponsibleUserId: number;
  statusActionType: StatusActionType;
  validMovements: MovementViewDto[] | undefined;
}

export enum StatusStateType {
  SUCCESS = 1,
  WARNING = 2,
  DANGER = 3,
  PENDING = 4,
  DEFAULT = 5,
}

export class ApplicationDashboardDtoPagedResultDto implements IApplicationDashboardDtoPagedResultDto {
  items: ApplicationDashboardDto[];
  totalCount: number;

  constructor(data?: IApplicationDashboardDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(ApplicationDashboardDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): ApplicationDashboardDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationDashboardDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): ApplicationDashboardDtoPagedResultDto {
    const json = this.toJSON();
    let result = new ApplicationDashboardDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IApplicationDashboardDtoPagedResultDto {
  items: ApplicationDashboardDto[];
  totalCount: number;
}

export class ApplicationDto implements IApplicationDto {
  id: number;
  fullName: string;
  nationalId: string;
  phoneNumber: string;
  email: string;
  statusName: string;
  reponsibleUserId: number;
  validMovements: MovementViewDto[];

  constructor(data?: IApplicationDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.fullName = _data["fullName"];
      this.nationalId = _data["nationalId"];
      this.phoneNumber = _data["phoneNumber"];
      this.email = _data["email"];
      this.statusName = _data["statusName"];
      this.reponsibleUserId = _data["reponsibleUserId"];
      if (Array.isArray(_data["validMovements"])) {
        this.validMovements = [] as any;
        for (let item of _data["validMovements"])
          this.validMovements.push(item);
      }
    }
  }

  static fromJS(data: any): ApplicationDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["fullName"] = this.fullName;
    data["nationalId"] = this.nationalId;
    data["phoneNumber"] = this.phoneNumber;
    data["email"] = this.email;
    data["statusName"] = this.statusName;
    data["reponsibleUserId"] = this.reponsibleUserId;
    if (Array.isArray(this.validMovements)) {
      data["ValidMovements"] = [];
      for (let item of this.validMovements) data["validMovements"].push(item);
    }
    return data;
  }

  clone(): ApplicationDto {
    const json = this.toJSON();
    let result = new ApplicationDto();
    result.init(json);
    return result;
  }
}

export interface IApplicationDto {
  id: number;
  fullName: string;
  nationalId: string;
  phoneNumber: string;
  email: string;
  statusName: string;
  reponsibleUserId: number;
  validMovements: MovementViewDto[];
}

export class MovementViewDto implements IMovementViewDto {
  movementId: number;
  nextStatusId: number;
  actionName: string;
  movementType: MovementType;
  deadlineHours: number;

  constructor(data?: IMovementViewDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.movementId = _data["movementId"];
      this.nextStatusId = _data["nextStatusId"];
      this.actionName = _data["actionName"];
      this.movementType = _data["movementType"];
      this.deadlineHours = _data["deadlineHours"];
    }
  }

  static fromJS(data: any): MovementViewDto {
    data = typeof data === "object" ? data : {};
    let result = new MovementViewDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["movementId"] = this.movementId;
    data["nextStatusId"] = this.nextStatusId;
    data["actionName"] = this.actionName;
    data["movementType"] = this.movementType;
    data["deadlineHours"] = this.deadlineHours;
    return data;
  }

  clone(): MovementViewDto {
    const json = this.toJSON();
    let result = new MovementViewDto();
    result.init(json);
    return result;
  }
}

export interface IMovementViewDto {
  movementId: number;
  nextStatusId: number;
  actionName: string;
  movementType: MovementType;
  deadlineHours: number;
}

export class CreateApplicationDto implements ICreateApplicationDto {
  id: number;
  personalInfo: PersonalInfoDto = new PersonalInfoDto();
  phoneNumber: string | undefined;
  email: string | undefined;
  communicationPath: string | undefined;
  howKnowCmp: string | undefined;
  migrationPurpose: MigrationPurpose;
  gradeId: number | undefined;
  desiredField: string | undefined;
  desiredCountryId: number | undefined;
  spouse: PersonalInfoDto = new PersonalInfoDto();
  spouseJob: string | undefined;
  yearlyBudget: number;
  currencyId: number | undefined;
  germanLangLevel: string | undefined;
  englishLangLevel: string | undefined;
  frenchLangLevel: string | undefined;
  langDocType: string | undefined;
  langDocLevel: string | undefined;
  notes: string | undefined;
  awarenessSourceId: number | undefined;
  referrerName: string | undefined;
  tags: string[] | undefined;
  childs: ChildDto[] | undefined = [];
  educationInfos: ApplicationEducationalInfoDto[] | undefined = [];
  employeementInfos: EmploymentInfoDto[] | undefined = [];

  constructor(data?: ICreateApplicationDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.personalInfo = _data["personalInfo"]
        ? PersonalInfoDto.fromJS(_data["personalInfo"])
        : <any>undefined;
      this.phoneNumber = _data["phoneNumber"];
      this.email = _data["email"];
      this.communicationPath = _data["communicationPath"];
      this.howKnowCmp = _data["howKnowCmp"];
      this.migrationPurpose = _data["migrationPurpose"];
      this.gradeId = _data["gradeId"];
      this.desiredField = _data["desiredField"];
      this.desiredCountryId = _data["desiredCountryId"];
      this.spouse = _data["spouse"]
        ? PersonalInfoDto.fromJS(_data["spouse"])
        : <any>undefined;
      this.spouseJob = _data["spouseJob"];
      this.yearlyBudget = _data["yearlyBudget"];
      this.currencyId = _data["currencyId"];
      this.germanLangLevel = _data["germanLangLevel"];
      this.englishLangLevel = _data["englishLangLevel"];
      this.frenchLangLevel = _data["frenchLangLevel"];
      this.langDocType = _data["langDocType"];
      this.langDocLevel = _data["langDocLevel"];
      this.notes = _data["notes"];
      this.awarenessSourceId = _data["awarenessSourceId"];
      this.referrerName = _data["referrerName"];
      if (Array.isArray(_data["tags"])) {
        this.tags = [] as any;
        for (let item of _data["tags"]) this.tags.push(item);
      }
      if (Array.isArray(_data["childs"])) {
        this.childs = [] as any;
        for (let item of _data["childs"])
          this.childs.push(ChildDto.fromJS(item));
      }
      if (Array.isArray(_data["educationInfos"])) {
        this.educationInfos = [] as any;
        for (let item of _data["educationInfos"])
          this.educationInfos.push(ApplicationEducationalInfoDto.fromJS(item));
      }
      if (Array.isArray(_data["employeementInfos"])) {
        this.employeementInfos = [] as any;
        for (let item of _data["employeementInfos"])
          this.employeementInfos.push(EmploymentInfoDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): CreateApplicationDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateApplicationDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["personalInfo"] = this.personalInfo
      ? this.personalInfo.toJSON()
      : <any>undefined;
    data["phoneNumber"] = this.phoneNumber;
    data["email"] = this.email;
    data["communicationPath"] = this.communicationPath;
    data["howKnowCmp"] = this.howKnowCmp;
    data["migrationPurpose"] = this.migrationPurpose;
    data["gradeId"] = this.gradeId;
    data["desiredField"] = this.desiredField;
    data["desiredCountryId"] = this.desiredCountryId;
    data["spouse"] = this.spouse ? this.spouse.toJSON() : <any>undefined;
    data["spouseJob"] = this.spouseJob;
    data["yearlyBudget"] = this.yearlyBudget;
    data["currencyId"] = this.currencyId;
    data["germanLangLevel"] = this.germanLangLevel;
    data["englishLangLevel"] = this.englishLangLevel;
    data["frenchLangLevel"] = this.frenchLangLevel;
    data["langDocType"] = this.langDocType;
    data["langDocLevel"] = this.langDocLevel;
    data["notes"] = this.notes;
    data["awarenessSourceId"] = this.awarenessSourceId;
    data["referrerName"] = this.referrerName;
    if (Array.isArray(this.tags)) {
      data["tags"] = [];
      for (let item of this.tags) data["tags"].push(item);
    }
    if (Array.isArray(this.childs)) {
      data["childs"] = [];
      for (let item of this.childs) data["childs"].push(item.toJSON());
    }
    if (Array.isArray(this.educationInfos)) {
      data["educationInfos"] = [];
      for (let item of this.educationInfos)
        data["educationInfos"].push(item.toJSON());
    }
    if (Array.isArray(this.employeementInfos)) {
      data["employeementInfos"] = [];
      for (let item of this.employeementInfos)
        data["employeementInfos"].push(item.toJSON());
    }
    return data;
  }

  clone(): CreateApplicationDto {
    const json = this.toJSON();
    let result = new CreateApplicationDto();
    result.init(json);
    return result;
  }
}

export interface ICreateApplicationDto {
  id: number;
  personalInfo: PersonalInfoDto;
  phoneNumber: string | undefined;
  email: string | undefined;
  communicationPath: string | undefined;
  howKnowCmp: string | undefined;
  migrationPurpose: MigrationPurpose;
  gradeId: number | undefined;
  desiredField: string | undefined;
  desiredCountryId: number | undefined;
  spouse: PersonalInfoDto;
  spouseJob: string | undefined;
  yearlyBudget: number;
  currencyId: number | undefined;
  germanLangLevel: string | undefined;
  englishLangLevel: string | undefined;
  frenchLangLevel: string | undefined;
  langDocType: string | undefined;
  langDocLevel: string | undefined;
  notes: string | undefined;
  awarenessSourceId: number | undefined;
  referrerName: string | undefined;
  tags: string[] | undefined;
  childs: ChildDto[] | undefined;
  educationInfos: ApplicationEducationalInfoDto[] | undefined;
  employeementInfos: EmploymentInfoDto[] | undefined;
}

export class PersonalInfoDto implements IPersonalInfoDto {
  firstName: string;
  lastName: string;
  gender: boolean;
  birthDate: Date;
  birthProvinceId: number;
  birthCityId: number;
  isMarried: boolean;
  identityNo: string;
  nationalId: string;
  militaryStatusId: number;

  constructor(data?: IPersonalInfoDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.firstName = _data["firstName"];
      this.lastName = _data["lastName"];
      this.gender = _data["gender"];
      this.birthDate = _data["birthDate"];
      this.birthProvinceId = _data["birthProvinceId"];
      this.birthCityId = _data["birthCityId"];
      this.isMarried = _data["isMarried"];
      this.identityNo = _data["identityNo"];
      this.nationalId = _data["nationalId"];
      this.militaryStatusId = _data["militaryStatusId"];
    }
  }

  static fromJS(data: any): PersonalInfoDto {
    data = typeof data === "object" ? data : {};
    let result = new PersonalInfoDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["firstName"] = this.firstName;
    data["lastName"] = this.lastName;
    data["gender"] = this.gender;
    data["birthDate"] = this.birthDate;
    data["birthProvinceId"] = this.birthProvinceId;
    data["birthCityId"] = this.birthCityId;
    data["isMarried"] = this.isMarried;
    data["identityNo"] = this.identityNo;
    data["nationalId"] = this.nationalId;
    data["militaryStatusId"] = this.militaryStatusId;
    return data;
  }

  clone(): PersonalInfoDto {
    const json = this.toJSON();
    let result = new PersonalInfoDto();
    result.init(json);
    return result;
  }
}

export interface IPersonalInfoDto {
  firstName: string;
  lastName: string;
  gender: boolean;
  birthDate: Date;
  birthProvinceId: number;
  birthCityId: number;
  isMarried: boolean;
  identityNo: string;
  nationalId: string;
  militaryStatusId: number;
}

export enum MigrationPurpose {
  Education = 1,
  Work = 2,
}

export enum MovementType {
  CONFIRM = 1,
  REJECT = 2,
}

export class ChildDto {
  name: string;
  age: number;
  gradeId: number;

  constructor(data?: IChildDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.name = _data["name"];
      this.age = _data["age"];
      this.gradeId = _data["gradeId"];
    }
  }

  static fromJS(data: any): ChildDto {
    data = typeof data === "object" ? data : {};
    let result = new ChildDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["name"] = this.name;
    data["age"] = this.age;
    data["gradeId"] = this.gradeId;
    return data;
  }

  clone(): ChildDto {
    const json = this.toJSON();
    let result = new ChildDto();
    result.init(json);
    return result;
  }
}

export interface IChildDto {
  name: string;
  age: number;
  gradeId: number;
}

export class ApplicationEducationalInfoDto implements IApplicationEducationalInfoDto {
  gradeId: number;
  educationField: string;
  educationInstituateName: string;
  startDate: Date;
  endDate: Date;
  average: number;
  description: string;

  constructor(data?: IApplicationEducationalInfoDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.gradeId = _data["gradeId"];
      this.educationField = _data["educationField"];
      this.educationInstituateName = _data["educationInstituateName"];
      this.startDate = _data["startDate"];
      this.endDate = _data["endDate"];
      this.average = _data["average"];
      this.description = _data["description"];
    }
  }

  static fromJS(data: any): ApplicationEducationalInfoDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationEducationalInfoDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["gradeId"] = this.gradeId;
    data["educationField"] = this.educationField;
    data["educationInstituateName"] = this.educationInstituateName;
    data["startDate"] = this.startDate;
    data["endDate"] = this.endDate;
    data["average"] = this.average;
    data["description"] = this.description;
    return data;
  }

  clone(): ApplicationEducationalInfoDto {
    const json = this.toJSON();
    let result = new ApplicationEducationalInfoDto();
    result.init(json);
    return result;
  }
}

export interface IApplicationEducationalInfoDto {
  gradeId: number;
  educationField: string;
  educationInstituateName: string;
  startDate: Date;
  endDate: Date;
  average: number;
  description: string;
}

export class EmploymentInfoDto implements IEmploymentInfoDto {
  companyName: string;
  position: string;
  fromDate: Date;
  toDate: Date;
  hasInsurance: boolean;

  constructor(data?: IEmploymentInfoDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.companyName = _data["companyName"];
      this.position = _data["position"];
      this.fromDate = _data["fromDate"];
      this.toDate = _data["toDate"];
      this.hasInsurance = _data["hasInsurance"];
    }
  }

  static fromJS(data: any): EmploymentInfoDto {
    data = typeof data === "object" ? data : {};
    let result = new EmploymentInfoDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["companyName"] = this.companyName;
    data["position"] = this.position;
    data["fromDate"] = this.fromDate;
    data["toDate"] = this.toDate;
    data["hasInsurance"] = this.hasInsurance;
    return data;
  }

  clone(): EmploymentInfoDto {
    const json = this.toJSON();
    let result = new EmploymentInfoDto();
    result.init(json);
    return result;
  }
}

export interface IEmploymentInfoDto {
  companyName: string;
  position: string;
  fromDate: Date;
  toDate: Date;
  hasInsurance: boolean;
}

export class ApplicationDtoPagedResultDto implements IApplicationDtoPagedResultDto {
  items: ApplicationDto[];
  totalCount: number;

  constructor(data?: IApplicationDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(ApplicationDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): ApplicationDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): ApplicationDtoPagedResultDto {
    const json = this.toJSON();
    let result = new ApplicationDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IApplicationDtoPagedResultDto {
  items: ApplicationDto[];
  totalCount: number;
}

export class ApplicationStatusLogDtoPagedResultDto implements IApplicationStatusLogDtoPagedResultDto {
  items: ApplicationStatusLogDto[] | undefined;
  totalCount: number;

  constructor(data?: ApplicationStatusLogDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(ApplicationStatusLogDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): ApplicationStatusLogDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationStatusLogDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): ApplicationStatusLogDtoPagedResultDto {
    const json = this.toJSON();
    let result = new ApplicationStatusLogDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IApplicationStatusLogDtoPagedResultDto {
  items: ApplicationStatusLogDto[] | undefined;
  totalCount: number;
}

export class AddMovementDto implements IAddMovementDto {
  movementId: number;
  applicationId: number;
  nextStatusId: number;
  actionName: string;
  movementType: MovementType;
  responsibleUserId: number;
  actionDescription: string;
  nextActionDescription: string;
  changeType: StatusChangeType;
  nextFollowupDate: Date;

  tags: string[] | undefined = [];

  constructor(data?: IAddMovementDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.movementId = _data["movementId"];
      this.nextStatusId = _data["nextStatusId"];
      this.actionName = _data["actionName"];
      this.movementType = _data["movementType"];
      this.applicationId = _data["applicationId"];
      this.responsibleUserId = _data["responsibleUserId"];
      this.actionDescription = _data["actionDescription"];
      this.nextActionDescription = _data["nextActionDescription"];
      this.nextFollowupDate = _data["nextFollowupDate"];
      this.changeType = _data["changeType"];
      if (Array.isArray(_data["tags"])) {
        this.tags = [] as any;
        for (let item of _data["tags"]) this.tags.push(item);
      }
    }
  }

  static fromJS(data: any): AddMovementDto {
    data = typeof data === "object" ? data : {};
    let result = new AddMovementDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["movementId"] = this.movementId;
    data["nextStatusId"] = this.nextStatusId;
    data["actionName"] = this.actionName;
    data["movementType"] = this.movementType;
    data["applicationId"] = this.applicationId;
    data["responsibleUserId"] = this.responsibleUserId;
    data["actionDescription"] = this.actionDescription;
    data["nextActionDescription"] = this.nextActionDescription;
    data["nextFollowupDate"] = this.nextFollowupDate;
    data["changeType"] = this.changeType;
    if (Array.isArray(this.tags)) {
      data["tags"] = [];
      for (let item of this.tags) data["tags"].push(item);
    }
    return data;
  }

  clone(): AddMovementDto {
    const json = this.toJSON();
    let result = new AddMovementDto();
    result.init(json);
    return result;
  }
}

export interface IAddMovementDto {
  movementId: number;
  nextStatusId: number;
  actionName: string;
  movementType: MovementType;
  applicationId: number;
  responsibleUserId: number;
  actionDescription: string;
  nextActionDescription: string;
  changeType: StatusChangeType;
  nextFollowupDate: Date;
  tags: string[] | undefined;
}

export class ApplicationStatusLogDto implements IApplicationStatusLogDto {
  id: number;
  actionName: string;
  sourceStatusName: string;
  destinationStatusName: string;
  issuerUserName: string;
  actionDescription: string;
  issueDate: string;
  changeType: StatusChangeType;

  constructor(data?: IApplicationStatusLogDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.actionName = _data["actionName"];
      this.sourceStatusName = _data["sourceStatusName"];
      this.destinationStatusName = _data["destinationStatusName"];
      this.issuerUserName = _data["issuerUserName"];
      this.actionDescription = _data["actionDescription"];
      this.issueDate = _data["issueDate"];
      this.changeType = _data["changeType"];
    }
  }

  static fromJS(data: any): ApplicationStatusLogDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationStatusLogDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["actionName"] = this.actionName;
    data["sourceStatusName"] = this.sourceStatusName;
    data["destinationStatusName"] = this.destinationStatusName;
    data["issuerUserName"] = this.issuerUserName;
    data["actionDescription"] = this.actionDescription;
    data["issueDate"] = this.issueDate;
    data["changeType"] = this.changeType;
    return data;
  }

  clone(): ApplicationStatusLogDto {
    const json = this.toJSON();
    let result = new ApplicationStatusLogDto();
    result.init(json);
    return result;
  }
}

export interface IApplicationStatusLogDto {
  id: number;
  actionName: string;
  sourceStatusName: string;
  destinationStatusName: string;
  issuerUserName: string;
  actionDescription: string;
  issueDate: string;
  changeType: StatusChangeType;
}

export enum StatusChangeType {
  CREATE,
  SEND_TO_NEXT_STEP,
  REJECT,
  ARCHIEVE,
  REFERRAL,
}

//#region ApplicationStatus
@Injectable()
export class ApplicationStatusServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  /**
   * @param body (optional)
   * @return Success
   */
  create(
    body: CreateApplicationStatusDto | undefined,
  ): Observable<ApplicationStatusDto> {
    let url_ = this.baseUrl + "/api/services/app/ApplicationStatus/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreate(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreate(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<ApplicationStatusDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationStatusDto>;
        }),
      );
  }

  protected processCreate(
    response: HttpResponseBase,
  ): Observable<ApplicationStatusDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApplicationStatusDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return Success
   */
  update(
    body: ApplicationStatusDto | undefined,
  ): Observable<ApplicationStatusDto> {
    let url_ = this.baseUrl + "/api/services/app/ApplicationStatus/Update";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("put", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdate(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdate(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<ApplicationStatusDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationStatusDto>;
        }),
      );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<ApplicationStatusDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApplicationStatusDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  delete(id: number | undefined): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/ApplicationStatus/Delete?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return this.http
      .request("delete", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDelete(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDelete(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        }),
      );
  }

  protected processDelete(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  get(id: number | undefined): Observable<ApplicationStatusDto> {
    let url_ = this.baseUrl + "/api/services/app/ApplicationStatus/Get?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGet(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGet(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<ApplicationStatusDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationStatusDto>;
        }),
      );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<ApplicationStatusDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApplicationStatusDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param keyword (optional)
   * @param isActive (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getAll(
    keyword: string | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
  ): Observable<ApplicationStatusDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/ApplicationStatus/GetAll?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<ApplicationStatusDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationStatusDtoPagedResultDto>;
        }),
      );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<ApplicationStatusDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApplicationStatusDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param keyword (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getActionRequiredStatuses(
    keyword: string | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
  ): Observable<ApplicationStatusDtoPagedResultDto> {
    let url_ =
      this.baseUrl +
      "/api/services/app/ApplicationStatus/GetActionRequiredStatuses?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetActionRequiredStatuses(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetActionRequiredStatuses(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<ApplicationStatusDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<ApplicationStatusDtoPagedResultDto>;
        }),
      );
  }

  protected processGetActionRequiredStatuses(
    response: HttpResponseBase,
  ): Observable<ApplicationStatusDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApplicationStatusDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }
}

export class CreateApplicationStatusDto implements ICreateApplicationStatusDto {
  name: string | undefined;
  order: number;
  description: string | undefined;
  requireAction: boolean;
  requiredActions: string | undefined;
  deadlineHours: number;
  stateType: StatusStateType;

  constructor(data?: ICreateApplicationStatusDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.name = _data["name"];
      this.order = _data["order"];
      this.description = _data["description"];
      this.requireAction = _data["requireAction"];
      this.requiredActions = _data["requiredActions"];
      this.deadlineHours = _data["deadlineHours"];
      this.stateType = _data["stateType"];
    }
  }

  static fromJS(data: any): CreateApplicationStatusDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateApplicationStatusDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["name"] = this.name;
    data["order"] = this.order;
    data["description"] = this.description;
    data["requireAction"] = this.requireAction;
    data["requiredActions"] = this.requiredActions;
    data["deadlineHours"] = this.deadlineHours;
    data["stateType"] = this.stateType;
    return data;
  }

  clone(): CreateApplicationStatusDto {
    const json = this.toJSON();
    let result = new CreateApplicationStatusDto();
    result.init(json);
    return result;
  }
}

export interface ICreateApplicationStatusDto {
  name: string | undefined;
  order: number;
  description: string | undefined;
  requireAction: boolean;
  requiredActions: string | undefined;
  deadlineHours: number;
  stateType: StatusStateType;
}

export class ApplicationStatusDto implements IApplicationStatusDto {
  id: number;
  name: string | undefined;
  order: number;
  description: string | undefined;
  requireAction: boolean;
  requiredActions: string | undefined;
  deadlineHours: number;
  actionType: StatusActionType;
  stateType: StatusStateType;

  constructor(data?: IApplicationStatusDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.name = _data["name"];
      this.order = _data["order"];
      this.description = _data["description"];
      this.requireAction = _data["requireAction"];
      this.requiredActions = _data["requiredActions"];
      this.deadlineHours = _data["deadlineHours"];
      this.actionType = _data["actionType"];
      this.stateType = _data["stateType"];
    }
  }

  static fromJS(data: any): ApplicationStatusDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationStatusDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["name"] = this.name;
    data["order"] = this.order;
    data["description"] = this.description;
    data["requireAction"] = this.requireAction;
    data["requiredActions"] = this.requiredActions;
    data["deadlineHours"] = this.deadlineHours;
    data["actionType"] = this.actionType;
    data["stateType"] = this.stateType;
    return data;
  }

  clone(): ApplicationStatusDto {
    const json = this.toJSON();
    let result = new ApplicationStatusDto();
    result.init(json);
    return result;
  }
}

export interface IApplicationStatusDto {
  id: number;
  name: string | undefined;
  order: number;
  description: string | undefined;
  requireAction: boolean;
  requiredActions: string | undefined;
  deadlineHours: number;
  actionType: StatusActionType;
  stateType: StatusStateType;
}

export class ApplicationStatusDtoPagedResultDto implements IApplicationStatusDtoPagedResultDto {
  items: ApplicationStatusDto[] | undefined;
  totalCount: number;

  constructor(data?: IApplicationStatusDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(ApplicationStatusDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): ApplicationStatusDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationStatusDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): ApplicationStatusDtoPagedResultDto {
    const json = this.toJSON();
    let result = new ApplicationStatusDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IApplicationStatusDtoPagedResultDto {
  items: ApplicationStatusDto[] | undefined;
  totalCount: number;
}

//#region StatusValidMovement
@Injectable()
export class StatusValidMovementServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  /**
   * @param body (optional)
   * @return Success
   */
  create(
    body: CreateStatusValidMovementDto | undefined,
  ): Observable<StatusValidMovementDto> {
    let url_ = this.baseUrl + "/api/services/app/StatusValidMovement/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreate(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreate(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<StatusValidMovementDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<StatusValidMovementDto>;
        }),
      );
  }

  protected processCreate(
    response: HttpResponseBase,
  ): Observable<StatusValidMovementDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = StatusValidMovementDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return Success
   */
  update(
    body: StatusValidMovementDto | undefined,
  ): Observable<StatusValidMovementDto> {
    let url_ = this.baseUrl + "/api/services/app/StatusValidMovement/Update";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("put", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdate(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdate(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<StatusValidMovementDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<StatusValidMovementDto>;
        }),
      );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<StatusValidMovementDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = StatusValidMovementDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  delete(id: number | undefined): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/StatusValidMovement/Delete?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return this.http
      .request("delete", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDelete(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDelete(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        }),
      );
  }

  protected processDelete(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  get(id: number | undefined): Observable<StatusValidMovementDto> {
    let url_ = this.baseUrl + "/api/services/app/StatusValidMovement/Get?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGet(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGet(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<StatusValidMovementDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<StatusValidMovementDto>;
        }),
      );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<StatusValidMovementDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = StatusValidMovementDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param keyword (optional)
   * @param isActive (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getAll(
    keyword: string | undefined,
    sourceStatusId: number | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
  ): Observable<StatusValidMovementDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/StatusValidMovement/GetAll?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (sourceStatusId === null)
      throw new Error("The parameter 'sourceStatusId' cannot be null.");
    else if (sourceStatusId !== undefined)
      url_ += "SourceStatusId=" + encodeURIComponent("" + sourceStatusId) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<StatusValidMovementDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<StatusValidMovementDtoPagedResultDto>;
        }),
      );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<StatusValidMovementDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 =
            StatusValidMovementDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }
}

export class CreateStatusValidMovementDto implements ICreateStatusValidMovementDto {
  actionName: string;
  sourceStatusId: number;
  destinationStatusId: number;
  movementType: MovementType;
  description: string;

  constructor(data?: ICreateStatusValidMovementDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.actionName = _data["actionName"];
      this.sourceStatusId = _data["sourceStatusId"];
      this.destinationStatusId = _data["destinationStatusId"];
      this.movementType = _data["movementType"];
      this.description = _data["description"];
    }
  }

  static fromJS(data: any): CreateStatusValidMovementDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateStatusValidMovementDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["actionName"] = this.actionName;
    data["sourceStatusId"] = this.sourceStatusId;
    data["destinationStatusId"] = this.destinationStatusId;
    data["movementType"] = this.movementType;
    data["description"] = this.description;
    return data;
  }

  clone(): CreateStatusValidMovementDto {
    const json = this.toJSON();
    let result = new CreateStatusValidMovementDto();
    result.init(json);
    return result;
  }
}

export interface ICreateStatusValidMovementDto {
  actionName: string;
  sourceStatusId: number;
  destinationStatusId: number;
  movementType: MovementType;
  description: string;
}

export class StatusValidMovementDto implements IStatusValidMovementDto {
  id: number;
  actionName: string;
  sourceStatusId: number;
  destinationStatusId: number;
  movementType: MovementType;
  description: string;

  sourceStatusName: string;
  destinationStatusName: string;

  constructor(data?: IStatusValidMovementDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.actionName = _data["actionName"];
      this.sourceStatusId = _data["sourceStatusId"];
      this.destinationStatusId = _data["destinationStatusId"];
      this.movementType = _data["movementType"];
      this.description = _data["description"];
      this.sourceStatusName = _data["sourceStatusName"];
      this.destinationStatusName = _data["destinationStatusName"];
    }
  }

  static fromJS(data: any): StatusValidMovementDto {
    data = typeof data === "object" ? data : {};
    let result = new StatusValidMovementDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["actionName"] = this.actionName;
    data["sourceStatusId"] = this.sourceStatusId;
    data["destinationStatusId"] = this.destinationStatusId;
    data["movementType"] = this.movementType;
    data["description"] = this.description;
    data["sourceStatusName"] = this.sourceStatusName;
    data["destinationStatusName"] = this.destinationStatusName;

    return data;
  }

  clone(): StatusValidMovementDto {
    const json = this.toJSON();
    let result = new StatusValidMovementDto();
    result.init(json);
    return result;
  }
}

export interface IStatusValidMovementDto {
  id: number;
  actionName: string;
  sourceStatusId: number;
  destinationStatusId: number;
  movementType: MovementType;
  description: string;

  sourceStatusName: string;
  destinationStatusName: string;
}

export class StatusValidMovementDtoPagedResultDto implements IStatusValidMovementDtoPagedResultDto {
  items: StatusValidMovementDto[] | undefined;
  totalCount: number;

  constructor(data?: IStatusValidMovementDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(StatusValidMovementDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): StatusValidMovementDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new StatusValidMovementDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): StatusValidMovementDtoPagedResultDto {
    const json = this.toJSON();
    let result = new StatusValidMovementDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IStatusValidMovementDtoPagedResultDto {
  items: StatusValidMovementDto[] | undefined;
  totalCount: number;
}

//#region MeetingSlot
@Injectable()
export class MeetingSlotServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  /**
   * @param body (optional)
   * @return Success
   */
  create(body: CreateMeetingSlotDto | undefined): Observable<MeetingSlotDto> {
    let url_ = this.baseUrl + "/api/services/app/MeetingSlot/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreate(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<MeetingSlotDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<MeetingSlotDto>;
        }),
      );
  }

  protected processCreate(
    response: HttpResponseBase,
  ): Observable<MeetingSlotDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MeetingSlotDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

   getReservationInfo(reservationId: string | undefined): Observable<ReservationInfoModel> {
        let url_ = this.baseUrl + "/api/services/app/MeetingSlot/GetReservationInfo?";
        if (reservationId === null)
            throw new Error("The parameter 'reservationId' cannot be null.");
        else if (reservationId !== undefined)
            url_ += "reservationId=" + encodeURIComponent("" + reservationId) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "text/plain"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetReservationInfo(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetReservationInfo(response_ as any);
                } catch (e) {
                    return _observableThrow(e) as any as Observable<ReservationInfoModel>;
                }
            } else
                return _observableThrow(response_) as any as Observable<ReservationInfoModel>;
        }));
    }

    protected processGetReservationInfo(response: HttpResponseBase): Observable<ReservationInfoModel> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = ReservationInfoModel.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf(null as any);
    }

  getAvailableSlots(
    expertId: number | undefined,
    slotDate: moment.Moment | undefined,
  ): Observable<MeetingSlotDto[]> {
    let url_ =
      this.baseUrl + "/api/services/app/MeetingSlot/GetAvailableSlots?";
    if (expertId === null)
      throw new Error("The parameter 'expertId' cannot be null.");
    else if (expertId !== undefined)
      url_ += "ExpertId=" + encodeURIComponent("" + expertId) + "&";
    if (slotDate === null)
      throw new Error("The parameter 'slotDate' cannot be null.");
    else if (slotDate !== undefined)
      url_ +=
        "SlotDate=" +
        encodeURIComponent(slotDate ? "" + slotDate.toISOString() : "") +
        "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAvailableSlots(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAvailableSlots(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<MeetingSlotDto[]>;
            }
          } else
            return _observableThrow(response_) as any as Observable<
              MeetingSlotDto[]
            >;
        }),
      );
  }

  protected processGetAvailableSlots(
    response: HttpResponseBase,
  ): Observable<MeetingSlotDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(MeetingSlotDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

 /**
     * @param body (optional)
     * @return Success
     */
    addMeetingRequest(body: MeetingRequestModel | undefined): Observable<string> {
        let url_ = this.baseUrl + "/api/services/app/MeetingSlot/AddMeetingRequest";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json-patch+json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processAddMeetingRequest(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processAddMeetingRequest(response_ as any);
                } catch (e) {
                    return _observableThrow(e) as any as Observable<string>;
                }
            } else
                return _observableThrow(response_) as any as Observable<string>;
        }));
    }

    protected processAddMeetingRequest(response: HttpResponseBase): Observable<string> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = resultData200 !== undefined ? resultData200 : <any>null;

            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf(null as any);
    }

    /**
     * @param reservationId (optional)
     * @param meetingTypeId (optional)
     * @return Success
     */
    confirmMeetingRequestByExpert(reservationId: number | undefined, meetingTypeId: number | undefined): Observable<boolean> {
        let url_ = this.baseUrl + "/api/services/app/MeetingSlot/ConfirmMeetingRequestByExpert?";
        if (reservationId === null)
            throw new Error("The parameter 'reservationId' cannot be null.");
        else if (reservationId !== undefined)
            url_ += "reservationId=" + encodeURIComponent("" + reservationId) + "&";
        if (meetingTypeId === null)
            throw new Error("The parameter 'meetingTypeId' cannot be null.");
        else if (meetingTypeId !== undefined)
            url_ += "meetingTypeId=" + encodeURIComponent("" + meetingTypeId) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processConfirmMeetingRequestByExpert(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processConfirmMeetingRequestByExpert(response_ as any);
                } catch (e) {
                    return _observableThrow(e) as any as Observable<boolean>;
                }
            } else
                return _observableThrow(response_) as any as Observable<boolean>;
        }));
    }

    protected processConfirmMeetingRequestByExpert(response: HttpResponseBase): Observable<boolean> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = resultData200 !== undefined ? resultData200 : <any>null;

            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf(null as any);
    }

    /**
     * @param reservationId (optional)
     * @return Success
     */
    rejectMeetingRequestByExpert(reservationId: number | undefined): Observable<boolean> {
        let url_ = this.baseUrl + "/api/services/app/MeetingSlot/RejectMeetingRequestByExpert?";
        if (reservationId === null)
            throw new Error("The parameter 'reservationId' cannot be null.");
        else if (reservationId !== undefined)
            url_ += "reservationId=" + encodeURIComponent("" + reservationId) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processRejectMeetingRequestByExpert(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processRejectMeetingRequestByExpert(response_ as any);
                } catch (e) {
                    return _observableThrow(e) as any as Observable<boolean>;
                }
            } else
                return _observableThrow(response_) as any as Observable<boolean>;
        }));
    }

    protected processRejectMeetingRequestByExpert(response: HttpResponseBase): Observable<boolean> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = resultData200 !== undefined ? resultData200 : <any>null;

            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf(null as any);
    }

  /**
   * @param body (optional)
   * @return Success
   */
  update(body: MeetingSlotDto | undefined): Observable<MeetingSlotDto> {
    let url_ = this.baseUrl + "/api/services/app/MeetingSlot/Update";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("put", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdate(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<MeetingSlotDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<MeetingSlotDto>;
        }),
      );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<MeetingSlotDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MeetingSlotDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  delete(id: number | undefined): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/MeetingSlot/Delete?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return this.http
      .request("delete", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDelete(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDelete(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        }),
      );
  }

  protected processDelete(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  get(id: number | undefined): Observable<MeetingSlotDto> {
    let url_ = this.baseUrl + "/api/services/app/MeetingSlot/Get?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGet(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGet(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<MeetingSlotDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<MeetingSlotDto>;
        }),
      );
  }

  protected processGet(response: HttpResponseBase): Observable<MeetingSlotDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MeetingSlotDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param keyword (optional)
   * @param date (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getAll(
    keyword: string | undefined,
    date: string | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
  ): Observable<MeetingSlotDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/MeetingSlot/GetAll?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (date === null) throw new Error("The parameter 'date' cannot be null.");
    else if (date !== undefined)
      url_ += "Date=" + encodeURIComponent(date ? "" + date : "") + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e,
              ) as any as Observable<MeetingSlotDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_,
            ) as any as Observable<MeetingSlotDtoPagedResultDto>;
        }),
      );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<MeetingSlotDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MeetingSlotDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return _observableOf(null as any);
  }
}


export class ReservationInfoModel implements IReservationInfoModel {
    meetingDate: Date;
    expireDate: Date;
    description: string | undefined;

    constructor(data?: IReservationInfoModel) {
        if (data) {
            for (var property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.meetingDate = _data["meetingDate"];
            this.expireDate = _data["expireDate"];
            this.description = _data["description"];
        }
    }

    static fromJS(data: any): ReservationInfoModel {
        data = typeof data === 'object' ? data : {};
        let result = new ReservationInfoModel();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["meetingDate"] = this.meetingDate;
        data["expireDate"] = this.expireDate;
        data["description"] = this.description;
        return data;
    }

    clone(): ReservationInfoModel {
        const json = this.toJSON();
        let result = new ReservationInfoModel();
        result.init(json);
        return result;
    }
}

export interface IReservationInfoModel {
    meetingDate: Date;
    expireDate: Date;
    description: string | undefined;
}

export class MeetingRequestModel implements IMeetingRequestModel {
    slotId: number;
    description: string | undefined;
    phoneNumber: string | undefined;
    applicationId: number | undefined;
    meetingTypeId: number | undefined;

    constructor(data?: IMeetingRequestModel) {
        if (data) {
            for (var property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.slotId = _data["slotId"];
            this.description = _data["description"];
            this.phoneNumber = _data["phoneNumber"];
            this.applicationId = _data["applicationId"];
            this.meetingTypeId = _data["meetingTypeId"];
        }
    }

    static fromJS(data: any): MeetingRequestModel {
        data = typeof data === 'object' ? data : {};
        let result = new MeetingRequestModel();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["slotId"] = this.slotId;
        data["description"] = this.description;
        data["phoneNumber"] = this.phoneNumber;
        data["applicationId"] = this.applicationId;
        data["meetingTypeId"] = this.meetingTypeId;
        return data;
    }

    clone(): MeetingRequestModel {
        const json = this.toJSON();
        let result = new MeetingRequestModel();
        result.init(json);
        return result;
    }
}

export interface IMeetingRequestModel {
    slotId: number;
    description: string | undefined;
    phoneNumber: string | undefined;
    applicationId: number | undefined;
    meetingTypeId: number | undefined;
}

export class CreateMeetingSlotDto implements ICreateMeetingSlotDto {
  expertId: string;
  times: TimeSlot[];

  constructor(data?: ICreateMeetingSlotDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.expertId = _data["expertId"];

      if (Array.isArray(_data["times"])) {
        this.times = [] as any;
        for (let item of _data["times"]) this.times.push(TimeSlot.fromJS(item));
      }
    }
  }

  static fromJS(data: any): CreateMeetingSlotDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateMeetingSlotDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["expertId"] = this.expertId;

    if (Array.isArray(this.times)) {
      data["times"] = [];
      for (let item of this.times) data["times"].push(item.toJSON());
    }
    return data;
  }

  clone(): CreateMeetingSlotDto {
    const json = this.toJSON();
    let result = new CreateMeetingSlotDto();
    result.init(json);
    return result;
  }
}

export interface ICreateMeetingSlotDto {
  expertId: string;
  times: TimeSlot[];
}

export class TimeSlot implements ITimeSlot {
  fromDate: string;
  toDate: string;
  fromTime: string;
  toTime: string;
  slotCount: number;
  breakTimeDuration: number;
  weekDays: number[] | undefined;

  constructor(data?: ITimeSlot) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.fromDate = _data["fromDate"];
      this.toDate = _data["toDate"];
      this.fromTime = _data["fromTime"];
      this.toTime = _data["toTime"];
      this.slotCount = _data["slotCount"];
      this.breakTimeDuration = _data["breakTimeDuration"];
      if (Array.isArray(_data["weekDays"])) {
        this.weekDays = [] as any;
        for (let item of _data["weekDays"]) this.weekDays.push(item);
      }
    }
  }

  static fromJS(data: any): TimeSlot {
    data = typeof data === "object" ? data : {};
    let result = new TimeSlot();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["fromDate"] = this.fromDate;
    data["toDate"] = this.toDate;
    data["fromTime"] = this.fromTime;
    data["toTime"] = this.toTime;
    data["slotCount"] = this.slotCount;
    data["breakTimeDuration"] = this.breakTimeDuration;
    if (Array.isArray(this.weekDays)) {
      data["weekDays"] = [];
      for (let item of this.weekDays) data["weekDays"].push(item);
    }
    return data;
  }

  clone(): TimeSlot {
    const json = this.toJSON();
    let result = new TimeSlot();
    result.init(json);
    return result;
  }
}

export interface ITimeSlot {
  fromDate: string;
  toDate: string;
  fromTime: string;
  toTime: string;
  slotCount: number;
  breakTimeDuration: number;
  weekDays: number[] | undefined;
}

export class MeetingSlotDto implements IMeetingSlotDto {
  id: number;
  expertId: number;
  expertName: string | undefined;
  applicationName: string | undefined;
  startTime: string;
  endTime: string;
  fromTime: string;
  toTime: string;
  isBooked: boolean;

  constructor(data?: IMeetingSlotDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.expertId = _data["expertId"];
      this.expertName = _data["expertName"];
      this.applicationName = _data["applicationName"];
      this.startTime = _data["startTime"];
      this.endTime = _data["endTime"];
      this.fromTime = _data["fromTime"];
      this.toTime = _data["toTime"];
      this.isBooked = _data["isBooked"];
    }
  }

  static fromJS(data: any): MeetingSlotDto {
    data = typeof data === "object" ? data : {};
    let result = new MeetingSlotDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["expertId"] = this.expertId;
    data["expertName"] = this.expertName;
    data["applicationName"] = this.applicationName;
    data["startTime"] = this.startTime;
    data["endTime"] = this.endTime;
    data["fromTime"] = this.fromTime;
    data["toTime"] = this.toTime;
    data["isBooked"] = this.isBooked;
    return data;
  }

  clone(): MeetingSlotDto {
    const json = this.toJSON();
    let result = new MeetingSlotDto();
    result.init(json);
    return result;
  }
}

export interface IMeetingSlotDto {
  id: number;
  expertId: number;
  expertName: string | undefined;
  applicationName: string | undefined;
  startTime: string;
  endTime: string;
  fromTime: string;
  toTime: string;
  isBooked: boolean;
}

export class MeetingSlotDtoPagedResultDto implements IMeetingSlotDtoPagedResultDto {
  items: MeetingSlotDto[] | undefined;
  totalCount: number;

  constructor(data?: IMeetingSlotDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(MeetingSlotDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): MeetingSlotDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new MeetingSlotDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): MeetingSlotDtoPagedResultDto {
    const json = this.toJSON();
    let result = new MeetingSlotDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IMeetingSlotDtoPagedResultDto {
  items: MeetingSlotDto[] | undefined;
  totalCount: number;
}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next("");
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = (event) => {
        observer.next((event.target as any).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}

export class ApiException extends Error {
  message: string;
  status: number;
  response: string;
  headers: { [key: string]: any };
  result: any;

  constructor(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result: any,
  ) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiException {
    return obj.isApiException === true;
  }
}

export enum StatusActionType {
  SetUser = 1,
  Meeting = 2,
  SendInfo = 3,
  FollowUp = 4,
}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any,
): Observable<any> {
  if (result !== null && result !== undefined) return _observableThrow(result);
  else
    return _observableThrow(
      new ApiException(message, status, response, headers, null),
    );
}
